import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  LogOut, ShoppingCart, FileText, MapPin, Euro, 
  Calendar, Loader2, ExternalLink, Check, Building2,
  Bell, Save, Star, CreditCard, Clock, BarChart3,
  TrendingUp, Wallet, Gift, X, Lock, Flame, Search, ArrowUpDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ALL_CATS } from "@/i18n/slugs";
import { InstallAppButton } from "@/components/InstallAppButton";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { ZoneSelector } from "@/components/ZoneSelector";
import { BankTransferDialog } from "@/components/BankTransferDialog";
import { Landmark, Users } from "lucide-react";
import { FAMILIES } from "@/i18n/taxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypeLabels = Object.fromEntries(ALL_CATS.map((c) => [c.key, c.fr.name]));

const budgetLabels = {
  "moins-5000": "< 5 000 €",
  "5000-10000": "5 000 - 10 000 €",
  "10000-20000": "10 000 - 20 000 €",
  "20000-50000": "20 000 - 50 000 €",
  "50000+": "> 50 000 €"
};

export default function ContractorDashboard() {
  const [contractor, setContractor] = useState(null);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [purchasedLeads, setPurchasedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferingId, setTransferingId] = useState(null);
  const [transferData, setTransferData] = useState(null);
  const [zoneFilter, setZoneFilter] = useState("mine");
  const [leadSort, setLeadSort] = useState("recent");
  const [citySearch, setCitySearch] = useState("");
  const [notifPrefs, setNotifPrefs] = useState({
    email_notifications: true,
    notification_types: [],
    notification_areas: []
  });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [changingPwd, setChangingPwd] = useState(false);
  const [myReviews, setMyReviews] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [cart, setCart] = useState([]);
  const [ordering, setOrdering] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [meRes, availableRes, purchasedRes, notifsRes, statsRes] = await Promise.all([
        axios.get(`${API}/contractors/me`, { withCredentials: true }),
        axios.get(`${API}/contractors/available-leads`, { withCredentials: true }),
        axios.get(`${API}/contractors/purchased-leads`, { withCredentials: true }),
        axios.get(`${API}/contractors/notification-preferences`, { withCredentials: true }),
        axios.get(`${API}/contractors/stats`, { withCredentials: true })
      ]);
      
      setContractor(meRes.data);
      setAvailableLeads(availableRes.data);
      setPurchasedLeads(purchasedRes.data);
      const prefs = notifsRes.data;
      if (!prefs.notification_types?.length) prefs.notification_types = meRes.data.project_types || [];
      setNotifPrefs(prefs);
      setStats(statsRes.data);
      try {
        const alertsRes = await axios.get(`${API}/contractors/notifications-log`, { withCredentials: true });
        setMyAlerts(alertsRes.data);
      } catch {}
      try {
        const ordersRes = await axios.get(`${API}/payments/orders`, { withCredentials: true });
        setPendingOrders((ordersRes.data.orders || []).filter((o) => o.status === "pending_validation"));
      } catch {}
      // Fetch reviews
      try {
        const reviewsRes = await axios.get(`${API}/reviews/contractor`, { withCredentials: true });
        setMyReviews(reviewsRes.data);
      } catch {}
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/espace-entrepreneur/login");
      } else {
        toast.error("Erreur lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  };

  const hasZones = (notifPrefs.notification_areas || []).length > 0;
  const inZoneLeads = availableLeads.filter((l) => l.in_zone !== false);
  const zoneLeads = hasZones && zoneFilter === "mine" ? inZoneLeads : availableLeads;
  const normalize = (v) => (v || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const q = normalize(citySearch.trim());
  const searchedLeads = q ? zoneLeads.filter((l) => normalize(l.city).includes(q) || (l.postal_code || "").startsWith(q)) : zoneLeads;
  const sorters = {
    recent: (a, b) => new Date(b.created_at) - new Date(a.created_at),
    price_asc: (a, b) => a.price - b.price || new Date(b.created_at) - new Date(a.created_at),
    price_desc: (a, b) => b.price - a.price || new Date(b.created_at) - new Date(a.created_at),
    urgent: (a, b) => (b.is_hot ? 1 : 0) - (a.is_hot ? 1 : 0) || (a.start_date && b.start_date ? new Date(a.start_date) - new Date(b.start_date) : (b.start_date ? 1 : 0) - (a.start_date ? 1 : 0)) || new Date(b.created_at) - new Date(a.created_at),
  };
  const visibleLeads = [...searchedLeads].sort(sorters[leadSort] || sorters.recent);
  const toggleCart = (lead) =>
    setCart((prev) => (prev.includes(lead.id) ? prev.filter((id) => id !== lead.id) : [...prev, lead.id]));
  const cartLeads = availableLeads.filter((l) => cart.includes(l.id));
  const cartTotal = cartLeads.reduce((s, l) => s + (Number(l.price) || 0), 0);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/contractors/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleSaveNotifs = async () => {
    setSavingNotifs(true);
    try {
      const notification_areas = notifPrefs.notification_areas || [];
      if (notifPrefs.notification_types.length === 0) {
        toast.error("Sélectionnez au moins un type de travaux");
        return;
      }
      await axios.put(`${API}/contractors/notification-preferences`, { ...notifPrefs, notification_areas }, { withCredentials: true });
      setNotifPrefs(prev => ({ ...prev, notification_areas }));
      toast.success("Types de travaux et préférences enregistrés");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingNotifs(false);
    }
  };

  const handleChangePassword = async () => {
    setChangingPwd(true);
    try {
      await axios.post(`${API}/contractors/change-password`, {
        current_password: pwdForm.current,
        new_password: pwdForm.next
      }, { withCredentials: true });
      toast.success("Mot de passe modifié avec succès ! Utilisez-le lors de votre prochaine connexion.");
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (error) {
      let msg = error.response?.data?.detail || "Erreur lors du changement de mot de passe";
      if (Array.isArray(msg)) msg = msg.map(m => m.msg || "").join(" ");
      toast.error(msg);
    } finally {
      setChangingPwd(false);
    }
  };

  const toggleNotifType = (type) => {
    setNotifPrefs(prev => ({
      ...prev,
      notification_types: prev.notification_types.includes(type)
        ? prev.notification_types.filter(t => t !== type)
        : [...prev.notification_types, type]
    }));
  };

  const handleOrderCart = async () => {
    if (cart.length === 0) return;
    setOrdering(true);
    try {
      const response = await axios.post(
        `${API}/payments/bank-transfer`,
        { lead_ids: cart, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (response.data.success) {
        setTransferData(response.data);
        setCart([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du bon de commande");
    } finally {
      setOrdering(false);
    }
  };

  const handleClaimFreeLead = async (leadId) => {
    setClaimingId(leadId);
    try {
      const response = await axios.post(
        `${API}/contractors/claim-free-lead`,
        { lead_id: leadId },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setShowClaimModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réclamation");
    } finally {
      setClaimingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">DM</span>
                </div>
                <span className="font-heading font-bold text-xl text-slate-900">
                  DevisMaison<span className="text-orange-500">.be</span>
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
                <Building2 className="w-4 h-4" />
                <span>{contractor?.company_name}</span>
              </div>
              <InstallAppButton />
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="contractor-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Bienvenue, {contractor?.company_name}
          </h1>
          <p className="text-slate-600 mt-1">
            Consultez les demandes de devis et achetez les leads qui vous intéressent
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-xs text-slate-500">Disponibles</span>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="kpi-available">{availableLeads.length}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-xs text-slate-500">Achetés</span>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="kpi-purchased">{stats?.total_purchased || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                <Wallet className="w-4 h-4 text-orange-600" />
              </div>
              <span className="text-xs text-slate-500">Total investi</span>
            </div>
            <p className="text-2xl font-heading font-bold text-orange-600" data-testid="kpi-spent">{stats?.total_spent || 0}€</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star className="w-4 h-4 text-yellow-600" />
              </div>
              <span className="text-xs text-slate-500">Note moyenne</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <p className="text-2xl font-heading font-bold text-slate-900" data-testid="kpi-rating">
                {stats?.avg_rating > 0 ? stats.avg_rating : "—"}
              </p>
              {stats?.avg_rating > 0 && <span className="text-xs text-slate-400">/5 ({stats.review_count})</span>}
            </div>
          </div>
        </div>

        {/* Loyalty Progress Bar */}
        {contractor && (
          <div className="mb-8 bg-white rounded-xl border border-slate-200 p-5" data-testid="loyalty-bar">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Gift className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-slate-900 text-sm">Programme fidélité</p>
                  <p className="text-xs text-slate-500">1 lead offert tous les 30 achats</p>
                </div>
              </div>
              {contractor.loyalty_free_available > 0 ? (
                <Button
                  onClick={() => setShowClaimModal(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                  data-testid="claim-free-lead-btn"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Réclamer mon lead gratuit !
                </Button>
              ) : (
                <span className="text-xs text-slate-400 hidden sm:inline" data-testid="loyalty-remaining">
                  Plus que {30 - (contractor.loyalty_progress || 0)} achat{30 - (contractor.loyalty_progress || 0) > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="relative">
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    contractor.loyalty_free_available > 0
                      ? "bg-gradient-to-r from-purple-500 to-purple-600"
                      : "bg-gradient-to-r from-orange-400 to-orange-500"
                  }`}
                  style={{ width: `${contractor.loyalty_free_available > 0 ? 100 : ((contractor.loyalty_progress || 0) / 30) * 100}%` }}
                  data-testid="loyalty-progress-fill"
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] text-slate-400 font-medium" data-testid="loyalty-counter">
                  {contractor.loyalty_progress || 0}/30
                </span>
                {contractor.free_leads_claimed > 0 && (
                  <span className="text-[10px] text-purple-500 font-medium">
                    {contractor.free_leads_claimed} lead{contractor.free_leads_claimed > 1 ? "s" : ""} offert{contractor.free_leads_claimed > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue={["available", "purchased", "notifications", "reviews", "stats"].includes(new URLSearchParams(window.location.search).get("tab")) ? new URLSearchParams(window.location.search).get("tab") : "available"} className="space-y-6">
          <TabsList className="flex flex-wrap h-auto justify-start gap-1 w-full sm:w-auto">
            <TabsTrigger value="available" data-testid="available-leads-tab">
              Leads disponibles ({availableLeads.length})
            </TabsTrigger>
            <TabsTrigger value="purchased" data-testid="purchased-leads-tab">
              Leads achetés ({purchasedLeads.length})
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="notifications-tab">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="reviews-tab">
              <Star className="w-4 h-4 mr-2" />
              Avis ({myReviews.length})
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="stats-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiques
            </TabsTrigger>
          </TabsList>

          {/* Available Leads */}
          <TabsContent value="available">
            {pendingOrders.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="pending-orders-card">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <h3 className="font-heading font-semibold text-slate-900 text-sm">Bons de commande en attente de paiement</h3>
                </div>
                <div className="space-y-2">
                  {pendingOrders.map((o) => (
                    <div key={o.id} className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between bg-white rounded-lg border border-amber-100 px-3 py-2" data-testid={`pending-order-${o.id}`}>
                      <div className="text-sm">
                        <span className="font-mono font-bold text-slate-900">{o.order_number}</span>
                        <span className="text-slate-500"> · {o.lead_count} lead(s) · <span className="font-semibold text-slate-700">{Number(o.amount).toFixed(2)}€</span></span>
                      </div>
                      <a
                        href={`${API}/payments/orders/${o.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors shrink-0"
                        data-testid={`download-order-${o.id}`}
                      >
                        <FileText className="w-4 h-4" />Télécharger le bon de commande
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {availableLeads.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="leads-toolbar">
                {hasZones && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="zone-filter-bar">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <button type="button" onClick={() => setZoneFilter("mine")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${zoneFilter === "mine" ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
                      data-testid="zone-filter-mine">
                      Mes zones ({inZoneLeads.length})
                    </button>
                    <button type="button" onClick={() => setZoneFilter("all")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${zoneFilter === "all" ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
                      data-testid="zone-filter-all">
                      Toutes les zones ({availableLeads.length})
                    </button>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 sm:ml-auto sm:items-center">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                      placeholder="Ville ou code postal..."
                      autoComplete="off"
                      className="pl-9 pr-8 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-52"
                      data-testid="lead-city-search"
                    />
                    {citySearch && (
                      <button type="button" onClick={() => setCitySearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="lead-city-search-clear">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-slate-400 shrink-0" />
                    <select
                      value={leadSort}
                      onChange={(e) => setLeadSort(e.target.value)}
                      className="py-2 pl-2 pr-8 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto"
                      data-testid="lead-sort-select"
                    >
                      <option value="recent">Plus récents</option>
                      <option value="urgent">Urgents d'abord</option>
                      <option value="price_asc">Prix croissant</option>
                      <option value="price_desc">Prix décroissant</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
            {visibleLeads.length === 0 && availableLeads.length > 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="no-leads-in-zone">
                {q ? <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" /> : <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />}
                <p className="text-slate-500">{q ? `Aucun lead pour « ${citySearch.trim()} »` : "Aucun lead dans vos zones pour le moment"}</p>
                {q ? (
                  <button type="button" onClick={() => setCitySearch("")} className="mt-3 text-sm font-medium text-orange-600 hover:underline" data-testid="clear-search-link">
                    Effacer la recherche
                  </button>
                ) : (
                  <button type="button" onClick={() => setZoneFilter("all")} className="mt-3 text-sm font-medium text-orange-600 hover:underline" data-testid="show-all-zones-link">
                    Voir les {availableLeads.length} lead(s) de toutes les zones
                  </button>
                )}
              </div>
            ) : availableLeads.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun lead disponible pour le moment</p>
                <p className="text-sm text-slate-400 mt-2">
                  Les nouveaux leads correspondant à vos critères apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-shadow"
                    data-testid={`available-lead-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          {projectTypeLabels[lead.project_type] || lead.project_type}
                        </span>
                        {lead.is_hot && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 ml-1" data-testid={`lead-hot-badge-${lead.id}`}>
                            <Flame className="w-3 h-3" /> Urgent · démarre sous 30 jours
                          </span>
                        )}
                        {lead.client_confirmed && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 ml-1" data-testid={`lead-confirmed-badge-${lead.id}`}>
                            ✓ Confirmé par le client
                          </span>
                        )}
                        {lead.lead_number && (
                          <p className="font-mono text-xs text-slate-400 mt-1">{lead.lead_number}</p>
                        )}
                      </div>
                      <span className="text-2xl font-heading font-bold text-green-600">
                        {lead.price}€
                      </span>
                    </div>
                    
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                      {lead.project_description}
                    </p>
                    
                    <div className="space-y-2 text-sm text-slate-500 mb-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{lead.city} ({lead.postal_code}){lead.country && lead.country !== "BE" ? ` · ${lead.country}` : ""}</span>
                        {hasZones && !lead.in_zone && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`out-of-zone-${lead.id}`}>hors zone</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4" />
                        <span>Budget: {budgetLabels[lead.budget] || lead.budget}</span>
                      </div>
                      {lead.start_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-orange-500" />
                          <span>Début: {new Date(lead.start_date).toLocaleDateString("fr-BE")}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(lead.created_at).toLocaleDateString("fr-BE")}</span>
                      </div>
                      {(lead.sold_count > 0 || lead.reserved_count > 0) && (
                        <div className="flex items-center gap-2" data-testid={`lead-slots-${lead.id}`}>
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className="text-xs">
                            {lead.sold_count > 0 && `Déjà acheté par ${lead.sold_count}/5`}
                            {lead.sold_count > 0 && lead.reserved_count > 0 && " · "}
                            {lead.reserved_count > 0 && `${lead.reserved_count} réservé(s)`}
                            {" · "}
                            {lead.slots_left > 0 ? `${lead.slots_left} place(s) restante(s)` : "complet"}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      {lead.pending_transfer ? (
                        <div className="flex-1 flex items-center justify-center gap-2 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium py-2 px-3" data-testid={`transfer-pending-badge-${lead.id}`}>
                          <Clock className="w-4 h-4" />Virement en attente de validation
                        </div>
                      ) : lead.slots_left <= 0 ? (
                        <div className="flex-1 flex items-center justify-center gap-2 rounded-md bg-slate-100 border border-slate-200 text-slate-500 text-sm font-medium py-2 px-3" data-testid={`lead-full-badge-${lead.id}`}>
                          <Users className="w-4 h-4" />Complet (5 acheteurs)
                        </div>
                      ) : cart.includes(lead.id) ? (
                        <Button
                          onClick={() => toggleCart(lead)}
                          variant="outline"
                          className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                          data-testid={`remove-from-cart-${lead.id}`}
                        >
                          <Check className="w-4 h-4 mr-2" />Dans le panier · retirer
                        </Button>
                      ) : (
                        <Button
                          onClick={() => toggleCart(lead)}
                          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid={`add-to-cart-${lead.id}`}
                        >
                          <Landmark className="w-4 h-4 mr-2" />Ajouter au panier · {lead.price}€
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {cart.length > 0 && (
              <div className="sticky bottom-4 mt-4 z-20" data-testid="cart-bar">
                <div className="bg-slate-900 text-white rounded-xl shadow-xl px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div>
                    <p className="font-semibold" data-testid="cart-count">{cart.length} lead(s) sélectionné(s)</p>
                    <p className="text-sm text-slate-300" data-testid="cart-total">Total : <span className="font-bold text-orange-400">{cartTotal.toFixed(2)}€</span></p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setCart([])} className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent" data-testid="cart-clear">Vider</Button>
                    <Button onClick={handleOrderCart} disabled={ordering} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="cart-order-btn">
                      {ordering ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Préparation…</>) : (<><Landmark className="w-4 h-4 mr-2" />Commander ({cart.length})</>)}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Purchased Leads */}
          <TabsContent value="purchased">
            {purchasedLeads.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Vous n'avez pas encore acheté de leads</p>
                <p className="text-sm text-slate-400 mt-2">
                  Vos leads achetés apparaîtront ici avec les coordonnées complètes
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {purchasedLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-white rounded-xl border border-green-200 p-6"
                    data-testid={`purchased-lead-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Check className="w-3 h-3 mr-1" />
                          {projectTypeLabels[lead.project_type] || lead.project_type}
                        </span>
                        {lead.lead_number && <p className="font-mono text-xs text-slate-400 mt-1">{lead.lead_number}</p>}
                      </div>
                      <span className="text-sm text-slate-500">
                        {new Date(lead.created_at).toLocaleDateString("fr-BE")}
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="font-semibold text-slate-900">
                        {lead.first_name} {lead.last_name}
                      </h3>
                      <p className="text-sm text-slate-500">{lead.city} ({lead.postal_code})</p>
                    </div>
                    
                    <p className="text-slate-600 text-sm mb-4">
                      {lead.project_description}
                    </p>
                    
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Email:</span>
                        <a href={`mailto:${lead.email}`} className="text-orange-500 hover:underline">
                          {lead.email}
                        </a>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Téléphone:</span>
                        <a href={`tel:${lead.phone}`} className="text-orange-500 hover:underline">
                          {lead.phone}
                        </a>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Budget:</span>
                        <span className="font-medium text-slate-900">
                          {budgetLabels[lead.budget] || lead.budget}
                        </span>
                      </div>
                      {lead.start_date && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Début souhaité:</span>
                          <span className="font-medium text-slate-900">
                            {new Date(lead.start_date).toLocaleDateString("fr-BE")}
                          </span>
                        </div>
                      )}
                      {lead.preferred_contact_method && lead.preferred_contact_method !== "both" && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Contact préféré:</span>
                          <span className="font-medium text-slate-900">
                            {lead.preferred_contact_method === "phone" ? "Téléphone" : "Email"}
                          </span>
                        </div>
                      )}
                      {lead.preferred_contact_hours && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Heures préférées:</span>
                          <span className="font-medium text-slate-900">
                            {lead.preferred_contact_hours === "matin" ? "Matin (8h-12h)" :
                             lead.preferred_contact_hours === "apres-midi" ? "Après-midi (12h-17h)" :
                             lead.preferred_contact_hours === "soiree" ? "Soirée (17h-20h)" :
                             lead.preferred_contact_hours === "flexible" ? "Flexible" :
                             lead.preferred_contact_hours}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Files section */}
                    {lead.files && lead.files.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-500 mb-2">Documents joints :</p>
                        <div className="space-y-1">
                          {lead.files.map((f, i) => (
                            <a key={i} href={`${API}/leads/${lead.id}/files/${f}`} target="_blank" rel="noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-600 hover:underline" data-testid={`purchased-file-${i}`}>
                              <FileText className="w-4 h-4" />
                              {f.split("_").slice(2).join("_")}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <PushNotificationsCard />
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="recent-alerts-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"><Bell className="w-5 h-5 text-slate-600" /></div>
                <div>
                  <h3 className="font-heading font-bold text-slate-900">Dernières alertes reçues</h3>
                  <p className="text-xs text-slate-500">Historique des e-mails et notifications push envoyés à votre entreprise</p>
                </div>
              </div>
              {myAlerts.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="recent-alerts-empty">Aucune alerte envoyée pour le moment.</p>
              ) : (
                <div className="divide-y divide-slate-100" data-testid="recent-alerts-list">
                  {myAlerts.slice(0, 20).map((a, i) => (
                    <div key={i} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`alert-row-${i}`}>
                      <span className={`mt-0.5 shrink-0 inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        (a.kind || "").startsWith("unsold") ? "bg-amber-100 text-amber-700" : (a.kind || "").startsWith("purchase") ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                        {(a.kind || "").startsWith("unsold") ? `Rappel ${a.kind.endsWith("7d") ? "J+7" : "48h"}` : (a.kind || "").startsWith("purchase") ? "Achat" : a.kind === "test" ? "Test" : "Nouveau lead"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-slate-800 truncate">{a.subject}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(a.created_at).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {a.channel === "push" ? "Push" : "E-mail"}
                          {a.lead_number ? ` · ${a.lead_number}` : ""}{a.city ? ` · ${a.city}` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="notifications-panel">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900">
                    Préférences de notification
                  </h2>
                  <p className="text-sm text-slate-500">
                    Choisissez quand et pour quels projets recevoir des alertes par email
                  </p>
                </div>
              </div>

              {/* Toggle principal */}
              <div className="mb-8 p-4 bg-slate-50 rounded-xl flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">Notifications par email</p>
                  <p className="text-sm text-slate-500">Recevoir un email quand un nouveau lead correspond à vos critères</p>
                </div>
                <button
                  onClick={() => setNotifPrefs(prev => ({ ...prev, email_notifications: !prev.email_notifications }))}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    notifPrefs.email_notifications ? "bg-orange-500" : "bg-slate-300"
                  }`}
                  data-testid="notif-toggle"
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                      notifPrefs.email_notifications ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              <div className="space-y-8">
                  {/* Types de travaux */}
                  <div>
                    <Label className="text-slate-900 font-semibold mb-3 block">
                      Mes types de travaux ({notifPrefs.notification_types.length} sélectionné{notifPrefs.notification_types.length > 1 ? "s" : ""})
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Les leads de ces catégories apparaissent dans « Leads disponibles » et vous sont notifiés par e-mail.
                    </p>
                    <div className="space-y-4 max-h-96 overflow-y-auto border border-slate-200 rounded-xl p-3 sm:p-4" data-testid="notif-types-list">
                      {FAMILIES.map((f) => (
                        <div key={f.key}>
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{f.icon} {f.fr}</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                            {ALL_CATS.filter((c) => c.family === f.key).map((c) => (
                              <button
                                key={c.key}
                                type="button"
                                onClick={() => toggleNotifType(c.key)}
                                className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors text-left ${
                                  notifPrefs.notification_types.includes(c.key)
                                    ? "bg-orange-500 text-white border-orange-500"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
                                }`}
                                data-testid={`notif-type-${c.key}`}
                              >
                                {c.fr.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Zones géographiques */}
                  <div>
                    <Label className="text-slate-900 font-semibold mb-3 block">
                      Zones géographiques (optionnel)
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Choisissez vos pays, puis vos provinces belges ou départements français. Vide = toutes les zones.
                    </p>
                    <ZoneSelector value={notifPrefs.notification_areas || []} onChange={(areas) => setNotifPrefs(prev => ({ ...prev, notification_areas: areas }))} />
                  </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
                <Button
                  onClick={handleSaveNotifs}
                  disabled={savingNotifs}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-notifs-button"
                >
                  {savingNotifs ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les préférences
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Changer mon mot de passe */}
            <div className="bg-white rounded-xl border border-slate-200 p-8 mt-6" data-testid="change-password-panel">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Lock className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900">Changer mon mot de passe</h2>
                  <p className="text-sm text-slate-500">Définissez votre propre mot de passe (min. 8 caractères, 1 majuscule, 1 chiffre)</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
                <div>
                  <Label className="text-slate-700 mb-2 block">Mot de passe actuel</Label>
                  <PasswordInput value={pwdForm.current} onChange={(e) => setPwdForm(p => ({ ...p, current: e.target.value }))} placeholder="••••••••" data-testid="current-password-input" />
                </div>
                <div>
                  <Label className="text-slate-700 mb-2 block">Nouveau mot de passe</Label>
                  <PasswordInput value={pwdForm.next} onChange={(e) => setPwdForm(p => ({ ...p, next: e.target.value }))} placeholder="••••••••" data-testid="new-password-input" />
                  {pwdForm.next.length > 0 && (
                    <div className="mt-2 space-y-0.5 text-xs" data-testid="new-password-hints">
                      <p className={pwdForm.next.length >= 8 ? "text-green-700" : "text-slate-500"}>{pwdForm.next.length >= 8 ? "✓" : "○"} Au moins 8 caractères</p>
                      <p className={/[A-Z]/.test(pwdForm.next) ? "text-green-700" : "text-slate-500"}>{/[A-Z]/.test(pwdForm.next) ? "✓" : "○"} Une majuscule</p>
                      <p className={/\d/.test(pwdForm.next) ? "text-green-700" : "text-slate-500"}>{/\d/.test(pwdForm.next) ? "✓" : "○"} Un chiffre</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-slate-700 mb-2 block">Confirmer le nouveau</Label>
                  <PasswordInput value={pwdForm.confirm} onChange={(e) => setPwdForm(p => ({ ...p, confirm: e.target.value }))} placeholder="••••••••" data-testid="confirm-password-input" />
                  {pwdForm.confirm.length > 0 && pwdForm.confirm !== pwdForm.next && (
                    <p className="text-xs text-red-600 mt-2">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
              </div>
              <div className="mt-6">
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPwd || !pwdForm.current || pwdForm.next.length < 8 || !/[A-Z]/.test(pwdForm.next) || !/\d/.test(pwdForm.next) || pwdForm.next !== pwdForm.confirm}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="change-password-button"
                >
                  {changingPwd ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Modification...</>) : (<><Lock className="w-4 h-4 mr-2" />Changer le mot de passe</>)}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div data-testid="contractor-reviews-panel">
              {myReviews.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun avis pour le moment</p>
                  <p className="text-sm text-slate-400 mt-2">
                    Les avis de vos clients apparaîtront ici après validation
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-orange-500">
                        {(myReviews.reduce((acc, r) => acc + r.rating, 0) / myReviews.length).toFixed(1)}
                      </p>
                      <div className="flex gap-0.5 justify-center mt-1">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-4 h-4 ${s <= Math.round(myReviews.reduce((a,r) => a+r.rating,0)/myReviews.length) ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
                        ))}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{myReviews.length} avis</p>
                    </div>
                  </div>
                  {myReviews.map((review, index) => (
                    <div key={index} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`my-review-${index}`}>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
                        ))}
                        <span className="text-sm text-slate-400 ml-2">
                          {new Date(review.created_at).toLocaleDateString("fr-BE")}
                        </span>
                      </div>
                      <p className="text-slate-600 mb-2">"{review.comment}"</p>
                      <p className="text-sm font-medium text-slate-900">{review.reviewer_name}</p>
                      {review.project_type && (
                        <p className="text-xs text-slate-500">{projectTypeLabels[review.project_type] || review.project_type}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="space-y-6" data-testid="stats-panel">
              {!stats ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Loader2 className="w-8 h-8 text-orange-400 animate-spin mx-auto mb-4" />
                  <p className="text-slate-500">Chargement des statistiques...</p>
                </div>
              ) : (
                <>
                  {/* Monthly History Chart */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900">Historique des achats</h3>
                        <p className="text-xs text-slate-500">Derniers 6 mois</p>
                      </div>
                    </div>
                    {stats.monthly_history.every(m => m.count === 0) ? (
                      <p className="text-sm text-slate-400 text-center py-6">Aucun achat sur cette période</p>
                    ) : (
                      <div className="space-y-3">
                        {stats.monthly_history.map((month, i) => {
                          const maxCount = Math.max(...stats.monthly_history.map(m => m.count), 1);
                          return (
                            <div key={i} className="flex items-center gap-4" data-testid={`month-bar-${i}`}>
                              <span className="text-xs text-slate-500 w-20 shrink-0 text-right">{month.month}</span>
                              <div className="flex-1 bg-slate-100 rounded-full h-7 relative overflow-hidden">
                                <div
                                  className="bg-gradient-to-r from-orange-400 to-orange-500 h-full rounded-full transition-all duration-500 flex items-center justify-end px-3"
                                  style={{ width: `${Math.max((month.count / maxCount) * 100, month.count > 0 ? 15 : 0)}%` }}
                                >
                                  {month.count > 0 && (
                                    <span className="text-xs font-bold text-white">{month.count}</span>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs font-medium text-slate-700 w-16 shrink-0">{month.spent}€</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Two columns: Categories + Cities */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Purchases by Category */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="font-heading font-semibold text-slate-900">Par catégorie</h3>
                      </div>
                      {stats.purchases_by_category.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">Aucun achat</p>
                      ) : (
                        <div className="space-y-2.5">
                          {stats.purchases_by_category.map((cat, i) => {
                            const maxCat = stats.purchases_by_category[0]?.count || 1;
                            return (
                              <div key={i} className="flex items-center gap-3" data-testid={`cat-bar-${i}`}>
                                <span className="text-sm text-slate-700 w-32 shrink-0 truncate">{cat.category}</span>
                                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                                  <div
                                    className="bg-gradient-to-r from-blue-400 to-blue-500 h-full rounded-full flex items-center justify-end px-2"
                                    style={{ width: `${Math.max((cat.count / maxCat) * 100, 20)}%` }}
                                  >
                                    <span className="text-[10px] font-bold text-white">{cat.count}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Top Cities */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="font-heading font-semibold text-slate-900">Par ville</h3>
                      </div>
                      {stats.top_cities.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">Aucun achat</p>
                      ) : (
                        <div className="space-y-2.5">
                          {stats.top_cities.map((city, i) => {
                            const maxCity = stats.top_cities[0]?.count || 1;
                            return (
                              <div key={i} className="flex items-center gap-3" data-testid={`city-bar-${i}`}>
                                <span className="text-sm text-slate-700 w-32 shrink-0 truncate">{city.city}</span>
                                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                                  <div
                                    className="bg-gradient-to-r from-green-400 to-green-500 h-full rounded-full flex items-center justify-end px-2"
                                    style={{ width: `${Math.max((city.count / maxCity) * 100, 20)}%` }}
                                  >
                                    <span className="text-[10px] font-bold text-white">{city.count}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Free Leads History */}
                  {stats.free_leads_claimed > 0 && (
                    <div className="bg-white rounded-xl border border-purple-200 p-6">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Gift className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900">Leads offerts</h3>
                          <p className="text-xs text-purple-500">{stats.free_leads_claimed} lead{stats.free_leads_claimed > 1 ? "s" : ""} gratuit{stats.free_leads_claimed > 1 ? "s" : ""} obtenus</p>
                        </div>
                      </div>
                      <div className="space-y-2" data-testid="free-leads-history">
                        {stats.free_leads_history.map((fl, i) => (
                          <div key={fl.id || i} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Gift className="w-4 h-4 text-purple-500" />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{fl.lead_type}</p>
                                <p className="text-xs text-slate-500">{fl.lead_city}</p>
                              </div>
                            </div>
                            <span className="text-xs text-slate-400">
                              {fl.claimed_at ? new Date(fl.claimed_at).toLocaleDateString("fr-BE") : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment History */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="font-heading font-semibold text-slate-900">Historique des paiements</h3>
                    </div>
                    {stats.payment_history.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Aucun paiement</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="payment-history-table">
                          <thead>
                            <tr className="border-b border-slate-100">
                              <th className="text-left text-xs text-slate-500 font-medium pb-3 pr-4">Date</th>
                              <th className="text-left text-xs text-slate-500 font-medium pb-3 pr-4">Lead</th>
                              <th className="text-left text-xs text-slate-500 font-medium pb-3 pr-4">Référence</th>
                              <th className="text-right text-xs text-slate-500 font-medium pb-3 pr-4">Montant</th>
                              <th className="text-right text-xs text-slate-500 font-medium pb-3">Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.payment_history.map((tx) => (
                              <tr key={tx.id} className="border-b border-slate-50 last:border-0" data-testid={`payment-row-${tx.id}`}>
                                <td className="py-3 pr-4 text-slate-700">
                                  {tx.created_at ? new Date(tx.created_at).toLocaleDateString("fr-BE") : "—"}
                                </td>
                                <td className="py-3 pr-4">
                                  <span className="text-slate-900 font-medium">{tx.lead_type}</span>
                                  {tx.lead_city && <span className="text-slate-400 ml-1">— {tx.lead_city}</span>}
                                </td>
                                <td className="py-3 pr-4 font-mono text-xs text-orange-600">{tx.reference}</td>
                                <td className="py-3 pr-4 text-right font-bold text-slate-900">{tx.amount}€</td>
                                <td className="py-3 text-right">
                                  <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                                    tx.status === "confirmed" ? "bg-green-100 text-green-700" :
                                    tx.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                    "bg-red-100 text-red-700"
                                  }`}>
                                    {tx.status === "confirmed" ? "Confirmé" :
                                     tx.status === "pending" ? "En attente" : tx.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <BankTransferDialog data={transferData} api={API} onClose={() => { setTransferData(null); fetchData(); }} />

      {/* Claim Free Lead Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="claim-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Gift className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Choisissez votre lead gratuit</h2>
                  <p className="text-xs text-slate-500">Parmi vos catégories : {contractor?.project_types?.map(pt => projectTypeLabels[pt] || pt).join(", ")}</p>
                </div>
              </div>
              <button onClick={() => setShowClaimModal(false)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-claim-modal">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6">
              {(() => {
                const eligible = availableLeads.filter(lead =>
                  contractor?.project_types?.includes(lead.project_type)
                );
                if (eligible.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucun lead disponible dans vos catégories pour le moment</p>
                      <p className="text-xs text-slate-400 mt-1">Revenez plus tard quand de nouveaux leads seront publiés</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-3">
                    {eligible.map((lead) => (
                      <div
                        key={lead.id}
                        className="border border-slate-200 rounded-xl p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                        data-testid={`claim-lead-${lead.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                {projectTypeLabels[lead.project_type] || lead.project_type}
                              </span>
                              {lead.lead_number && <span className="font-mono text-[10px] text-slate-400">{lead.lead_number}</span>}
                            </div>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-1">{lead.project_description}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.city}</span>
                              <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{budgetLabels[lead.budget] || lead.budget}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleClaimFreeLead(lead.id)}
                            disabled={claimingId === lead.id}
                            className="bg-purple-600 hover:bg-purple-700 text-white shrink-0 ml-3"
                            data-testid={`claim-btn-${lead.id}`}
                          >
                            {claimingId === lead.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Gift className="w-3.5 h-3.5 mr-1" />
                                Réclamer
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
